/**
 * V1 → V2 redirect layer — CLIENT-SIDE shim for non-SSR (SPA) builds.
 *
 * The SSR path (`redirects.loader.ts`, wired into `server.ts`) only runs
 * when SSR is active. The `local` build configuration sets `ssr: false`, so under
 * `ng serve --configuration local` there is NO server middleware in the request path.
 * This shim runs the SAME exact-match logic (`redirects.ts`) in the browser
 * at bootstrap so the V1 → V2 mappings are honoured on SPA builds too.
 *
 * Because there is no server in a SPA build, this is a client-side `location.replace`
 * rather than a real 301 — but the matching is identical to the SSR path, so a hit
 * here mirrors the rule that would fire server-side.
 *
 * Two sources, checked in PRIORITY ORDER:
 *   1. The admin-managed redirects from `GET {apiUrl}/redirects` — small and
 *      authoritative. Checked FIRST; on a hit we redirect immediately.
 *   2. The generated bulk map at `/assets/redirects.json` (the V1 set) — large:
 *      5.99 MB measured. Consulted ONLY when the API has no match, and time-boxed.
 *
 * This ordering is deliberate: the API source is tiny and the one editors control,
 * so it must never be held hostage by the multi-megabyte static file (a slow or
 * stalled download of which would otherwise block every redirect). Checking the API
 * first also gives admin-managed entries precedence on path collisions.
 *
 * WHAT BOOTSTRAP WAITS FOR (BBS-0070)
 *
 * Only step 1. Step 2 is deliberately NOT awaited.
 *
 * This shim is called from a blocking app initializer, and Angular does not create
 * the root component until every such initializer settles. Awaiting the bulk map
 * therefore made hydration wait on a 6 MB uncompressed download — up to the full
 * 20-second timeout on a poor connection — during which the page was visible and
 * completely inert. That is the same dead-page symptom this ticket was raised for,
 * just caused by bandwidth instead of an exception.
 *
 * The consequence of not awaiting is that a bulk-map hit now redirects a moment
 * after the app renders rather than before, so an old URL can briefly flash the
 * Angular 404 before moving on. That is a deliberate trade: a visible flash on a
 * legacy URL, only on SPA builds (`local`, `staging`, `uat`, `development` — the
 * production build is SSR and skips this shim entirely), in exchange for never
 * holding hydration. The redirect itself still happens, and admin-managed
 * redirects — the ones editors actually maintain — are still applied before the
 * first paint.
 */
import { environment } from '@environment/environment';

import { buildRedirectIndex, matchRedirect, RedirectEntry } from './redirects';

const REDIRECTS_ASSET = '/assets/redirects.json';

/** Timeout for the (small) admin-managed redirects fetch. */
const API_TIMEOUT_MS = 8_000;

/** Timeout for the (large) generated static map fetch — bounded so it can never hang. */
const ASSET_TIMEOUT_MS = 20_000;

/**
 * Fetches a list of redirect entries from a URL. Never throws: any failure
 * (missing asset/endpoint, non-OK status, parse error, bad shape, timeout) resolves
 * to an empty array so a single bad source can't break the redirect map or bootstrap.
 */
async function fetchEntries(url: string, init?: RequestInit): Promise<RedirectEntry[]> {
  try {
    const response = await fetch(url, init);
    if (!response.ok) {
      return [];
    }

    const json = (await response.json()) as unknown;
    return Array.isArray(json) ? (json as RedirectEntry[]) : [];
  } catch {
    return [];
  }
}

/**
 * Whether a redirect destination is safe to hand to `window.location.replace`.
 *
 * Defence in depth, and a CLIENT-ONLY concern: the SSR path puts the destination
 * in a `Location:` header, where a `javascript:` value cannot execute. Here it
 * would run as script in our own origin, so the scheme is checked rather than
 * trusted. Reaching this requires control of the admin redirects table or of the
 * API response — but a destination is data from a remote source, and the cost of
 * validating it is one comparison.
 *
 * Root-relative paths and absolute `http(s)` URLs are both allowed, because both
 * are legitimate: the generated V1 map is entirely internal, while an
 * admin-managed redirect may deliberately point off-site. Allowing exactly what
 * the server allows also keeps the two layers' decisions in parity. Everything
 * else — `javascript:`, `data:`, `vbscript:`, an unparseable value — is refused.
 *
 * PATH-RELATIVE VALUES ARE REFUSED, and deliberately so. This check must judge
 * the destination the browser will actually visit, and the two disagree for any
 * relative value that is not root-relative: resolving `events/x` against
 * `window.location.origin` yields `/events/x`, but `location.replace('events/x')`
 * resolves against the CURRENT PATH and lands on `/whatever/dir/events/x`. The
 * old check validated the first and navigated to the second, so a malformed entry
 * sent the visitor somewhere unintended while logging as a clean, intentional
 * redirect. Only forms whose meaning is independent of the current path are
 * accepted; `//host/path` is protocol-relative — absolute in disguise — and is
 * refused for the same reason rather than silently inheriting our scheme.
 */
function isAllowedRedirectTarget(target: string): boolean {
  // The one relative form whose resolution is unambiguous, and identical here and
  // in `location.replace`. `//` is excluded: it is protocol-relative, not a path.
  if (target.startsWith('/') && !target.startsWith('//')) {
    return true;
  }

  try {
    // No base argument, so only a genuinely absolute URL parses. Anything relative
    // (or blank, or whitespace) throws here instead of being quietly resolved.
    const { protocol } = new URL(target);
    return protocol === 'https:' || protocol === 'http:';
  } catch {
    return false;
  }
}

/**
 * Builds an index from the given entries, matches the current path, and performs a
 * client-side redirect on a hit. Returns true if it redirected (so the caller can
 * stop), false otherwise.
 */
function applyRedirect(entries: RedirectEntry[], path: string): boolean {
  if (!entries.length) {
    return false;
  }

  const match = matchRedirect(buildRedirectIndex(entries), path);
  if (!match) {
    return false;
  }

  // 410 (Gone) carries no destination — surface the V2 not-found page locally.
  const target = match.status === 410 ? '/404' : match.to;

  if (!isAllowedRedirectTarget(target)) {
    // Logged with the path, not the rejected value, and treated as a miss so the
    // visitor simply carries on to the route they asked for.
    // eslint-disable-next-line no-console
    console.warn(`[Redirects] (client shim) refused an unsupported redirect target for ${path}.`);
    return false;
  }

  // eslint-disable-next-line no-console
  console.warn(`[Redirects] (client shim) ${path} → ${target} (status ${match.status}).`);

  window.location.replace(target);
  return true;
}

/**
 * Looks up the current path and, on a hit, performs a client-side redirect.
 *
 * The returned promise settles once the small admin-managed source has been
 * checked, so bootstrap waits only for that. The bulk map is started in the
 * background and applied whenever it arrives — see the note on what bootstrap
 * waits for at the top of this file.
 *
 * Never throws, and always settles: any failure degrades to a no-op so the app
 * still boots normally.
 */
export async function runRedirectShim(): Promise<void> {
  try {
    const path = window.location.pathname;
    const apiBase = environment.api_url.replace(/\/+$/, '');

    // 1. Admin-managed redirects first — fast and authoritative. Redirect on a hit
    //    without waiting for the large static map. `no-cache` revalidates, so an
    //    edit made in the admin takes effect immediately.
    const apiEntries = await fetchEntries(`${apiBase}/redirects`, {
      cache: 'no-cache',
      headers: { Accept: 'application/json', 'X-Request-Origin': 'web' },
      signal: AbortSignal.timeout(API_TIMEOUT_MS),
    });
    if (applyRedirect(apiEntries, path)) {
      return;
    }

    // 2. Fall back to the generated bulk map — NOT awaited, so it cannot hold
    //    hydration. Still time-boxed so a stalled download cannot leak forever.
    //
    //    No `cache` override here: the map is a build artefact that only changes
    //    when the app is redeployed, and `server.ts` serves it with a one-year
    //    `max-age`. The previous `no-cache` defeated that entirely and re-fetched
    //    all 6 MB from origin on every single page view.
    void fetchEntries(REDIRECTS_ASSET, { signal: AbortSignal.timeout(ASSET_TIMEOUT_MS) })
      .then((fileEntries) => applyRedirect(fileEntries, path))
      .catch(() => {
        // Never surface a redirect-lookup failure; the app boots regardless.
      });
  } catch {
    // Never block bootstrap on the shim.
  }
}
